import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SeriesDataService} from "../../services/series_data.service";
import {Series, SeriesSeriesTableData, SeriesSeriesData} from '../../_models/series';
import {catchError, Observable, of, Subject, combineLatest} from 'rxjs';
import {take, tap, concatMap} from 'rxjs/operators';
import {RelMappingResult} from "../../_typing/relationship-api-mapping";
import {Component} from "../../_models/component";
import {SeriesType} from '../../_models/series-type';
import {stub} from "../../lib/utils";
import {ListResponse} from "../../services/api/response-types";
import {SeriesSeries} from "../../_models/series-series";
import {ErrorBankService} from "../../services/error-bank.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {KeyMap} from "../../_typing/generic-types";
import {NotificationService} from "../../services/notification.service";

@Injectable({
    providedIn: 'root'
})
export class SeriesEstimateFormService {
    private readonly destroyRef = inject(DestroyRef);
    public $targetData: Subject<SeriesSeriesTableData> = new Subject();
    public $sourceData: Subject<SeriesSeriesTableData> = new Subject();
    source_series: SeriesSeries[];
    target_series: SeriesSeries[];
    target_data: SeriesSeriesData[];
    source_data: SeriesSeriesData[];

    constructor(private seriesData: SeriesDataService,
                private errorBank: ErrorBankService,
                private snackbar: MatSnackBar,
                private notification: NotificationService) {
    }

    getTargetSeries(series: Series): void {
        combineLatest(this.seriesData.getSeriesSeriesData(series.id), this.seriesData.getTargetSeries(series.id))
            .pipe(take(1),
                catchError((errors) => {
                    console.log('ERROR: SeriesEstimatesFormService (getEstimates) \n', errors);
                    return of(errors);
                }),
                takeUntilDestroyed(this.destroyRef)).subscribe(([series, series_series]) => {
            this.target_data = series;
            this.target_series = series_series.data;
            this.target_series.forEach(ss => {
                this.target_data.find(s => s.id === ss.relationships.target_series.data.id).positive_variance = ss.attributes.positive_variance;
            })
            this.$targetData.next({series: this.target_data, series_series: this.target_series});
        });
    }

    getSourceSeries(series: Series): void {
        combineLatest(this.seriesData.getSeriesSeriesData(series.id, 'target_series'), this.seriesData.getSourceSeries(series.id))
            .pipe(
                take(1),
                catchError((errors) => {
                    console.log('ERROR: SeriesEstimatesFormService (getEstimates) \n', errors);
                    return of(errors);
                }),
                takeUntilDestroyed(this.destroyRef)).subscribe(([series, series_series]) => {
            this.source_data = series;
            this.source_series = series_series.data;
            this.source_series.forEach(ss => {
                this.source_data.find(s => s.id === ss.relationships.source_series.data.id).positive_variance = ss.attributes.positive_variance || true;
            })
            this.$sourceData.next({series: this.source_data, series_series: this.source_series});
        });
    }

    newTargetSeries(component: Component, source_series: Series, series_type: SeriesType): void {
        const ctrl = this;
        let series = new Series();
        if (series_type) {
            series.relationships.series_type.data = stub<SeriesType>(series_type);
        }
        series.attributes.name = source_series.attributes.name + '_' + (series_type.attributes.abbreviation || series_type.attributes.name);
        series.attributes.description = (source_series.attributes.description || source_series.attributes.name) + ' ' + (series_type.attributes.name);
        const seriesForm = this.seriesData.upsertSeries(component, series, source_series);
        seriesForm.afterClosed().pipe(take(1)).subscribe(() => this.getTargetSeries(source_series));
    }

    newSourceSeries(component: Component, target_series: Series, new_series: SeriesSeriesData[], mapping_list: SeriesSeries[]): void {
        const ctrl = this;
        let series = new Series();
        const seriesForm = this.seriesData.upsertSeries(component, series);
        seriesForm.afterClosed().pipe(take(1), concatMap(series => {
            return this.seriesData.saveSourceSeries(target_series, series.series, true)
        })).subscribe(() => this.getSourceSeries(target_series));
    }

    saveSeriesSeries(series: Series, type: string, new_series: SeriesSeriesData[], mapping_list: SeriesSeries[]): Observable<RelMappingResult> {
        //Only available for new series - otherwise through estimates form
        let $series_series: Observable<RelMappingResult>;
        if (type === 'targets') {
            $series_series = this.seriesData.saveManyTargetSeries(series, this.target_data || [],
                new_series || [], mapping_list || [])
        } else {
            $series_series = this.seriesData.saveManySourceSeries(series, this.source_data || [],
                new_series || [], mapping_list || [])
        }

        return $series_series.pipe(
            tap((result: RelMappingResult) => {
                if (result.failures.length > 0) {
                    console.log("ERROR: SeriesEstimateFormService (saveSeriesSeries) ", result.failures);
                    this.notification.openError(`${result.successes.length} saved. ${result.failures.length} series link(s) failed to save. Please see the console for details. Try again or click refresh to reset selected items.`);
                }
            })
        );
    }

    /** Apply a partial-failure RelMappingResult to the diff baselines (source_data/target_data and
     * source_series/target_series) without re-emitting on $sourceData/$targetData. Returns the
     * reconciled mapping list so the component can update its own copy.
     * Why: emitting overwrites component.source_data/target_data — which is the user's selection
     * (including failed adds we want to retry). Reconciling the baselines silently lets the next
     * Save's diff retry exactly the failed items. */
    applyResultToBaseline(
        type: 'sources' | 'targets',
        result: RelMappingResult,
        previousMappingList: SeriesSeries[]
    ): SeriesSeries[] {
        const relationKey = type === 'sources' ? 'source_series' : 'target_series';
        const baselineSeries = (type === 'sources' ? this.source_data : this.target_data) || [];
        const baselineMappings = (type === 'sources' ? this.source_series : this.target_series) || [];

        const deletedSet = new Set(result.deletedIds);
        const deletedSeriesIds = new Set(
            (previousMappingList || [])
                .filter(m => deletedSet.has(m.id))
                .map(m => m.relationships[relationKey].data.id)
        );

        const reconciledMappings = baselineMappings
            .filter(ss => !deletedSet.has(ss.id))
            .concat(result.successes.map(s => s.data));

        const remainingSeries = baselineSeries.filter(s => !deletedSeriesIds.has(s.id));
        // saveMany's diff uses compare_ids (id-only). For successfully-added series we don't have
        // the full Series object here, so an {id} stub is sufficient as a baseline entry.
        const stubsToAdd = result.successes
            .map(s => s.data.relationships[relationKey].data)
            .filter(ref => !remainingSeries.some(r => r.id === ref.id))
            .map(ref => ({id: ref.id} as SeriesSeriesData));

        if (type === 'sources') {
            this.source_data = [...remainingSeries, ...stubsToAdd];
            this.source_series = reconciledMappings;
        } else {
            this.target_data = [...remainingSeries, ...stubsToAdd];
            this.target_series = reconciledMappings;
        }
        return reconciledMappings;
    }


    updateTargetSeries(series: Series, source_series: Series): void {
        const ctrl = this;
        const seriesForm = this.seriesData.upsertSeries(null, series);
        seriesForm.afterClosed().pipe(take(1)).subscribe(() => this.getTargetSeries(source_series));
    }

    updateSourceSeries(series: Series, target_series: Series): void {
        const ctrl = this;
        const seriesForm = this.seriesData.upsertSeries(null, series);
        seriesForm.afterClosed().pipe(take(1)).subscribe(() => this.getTargetSeries(target_series));
    }

    updateSeriesSeries(series_series) {
        this.seriesData.saveSeriesSeries(series_series).pipe(take(1))
            .subscribe(() => this.snackbar.open("Variance saved.", null, {duration: 3000}));
    }
}
